import { Box, Stack, Typography, useTheme } from '@mui/material';
import { APPROVAL_FLOW_INVOICE_TYPES, type ApprovalFlowInvoiceTypes } from '@vy/api-definition';
import { isNotNullOrUndefined, uniqueBy } from '@vy/utils';
import React from 'react';
import { ToggleChip } from '../../../../components/new-design/chip/ToggleChip';
import { SearchInput } from '../../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar-components/SearchInput';
import { useEffectOnce } from '../../../../hooks/use-effect-once';
import { useStateParams } from '../../../../hooks/use-state-params';
import { serializeStringArrayParam, deserializeEnumArrayParam } from '../../../../utils/params-formatting';

type ApprovalFlowInvoicesListHeaderProps = {
  onSearchChange?: (search: string) => void;
  onFiltersChange?: (filters: ApprovalFlowInvoiceTypes[]) => void;
};

const ApprovalFlowInvoicesListHeader: React.FC<ApprovalFlowInvoicesListHeaderProps> = ({
  onFiltersChange,
  onSearchChange,
}) => {
  const [filters, setFilters] = useSelectedFilters();
  const vyTheme = useTheme();

  useEffectOnce(() => {
    if (isNotNullOrUndefined(filters)) {
      onFiltersChange?.(filters);
    }
  });

  const handleFilterChange = (selectedFilter: ApprovalFlowInvoiceTypes, isToggled: boolean) => {
    const newFilters = isToggled
      ? uniqueBy([...(filters ?? []), selectedFilter], (filter) => filter)
      : (filters?.filter((filter) => filter !== selectedFilter) ?? []);
    onFiltersChange?.(newFilters);
    setFilters(newFilters);
  };

  const handleSearchChange = (search: string) => {
    onSearchChange?.(search);
  };

  return (
    <Stack
      direction="column"
      sx={{
        padding: 1,
        paddingX: 2
      }}>
      <Stack direction="row" sx={{
        paddingY: 2
      }}>
        <Typography variant="h6">Invoices</Typography>
      </Stack>
      <Stack direction="row">
        <SearchInput placeholder="Search" onChange={(event) => handleSearchChange(event.target.value)} />
      </Stack>
      <Box sx={{
        paddingY: 2
      }}>
        <Typography variant="subtitle2">Filter by tags</Typography>
        <Stack
          direction="row"
          sx={{
            gap: 1,
            paddingY: 1,
            flexWrap: "wrap"
          }}>
          <ToggleChip
            toggledColor={vyTheme.palette.common.black}
            toggledContrastColor={vyTheme.palette.common.white}
            initialToggle={filters?.includes(APPROVAL_FLOW_INVOICE_TYPES.OVERAGES)}
            onToggle={(toggleState) => handleFilterChange(APPROVAL_FLOW_INVOICE_TYPES.OVERAGES, toggleState)}
            label="Overages"
          />
        </Stack>
      </Box>
    </Stack>
  );
};

const MemoizedApprovalFlowInvoicesListHeader = React.memo(ApprovalFlowInvoicesListHeader);
export { MemoizedApprovalFlowInvoicesListHeader as ApprovalFlowInvoicesListHeader };

function useSelectedFilters() {
  return useStateParams<ApprovalFlowInvoiceTypes[]>(
    [APPROVAL_FLOW_INVOICE_TYPES.PREVIOUS_CYCLE],
    'filters',
    serializeStringArrayParam,
    deserializeEnumArrayParam(APPROVAL_FLOW_INVOICE_TYPES),
  );
}
